import AdminErrorInfo from "@cayo/shared/api/types/AdminErrorInfo";
import { Severity } from "@cayo/shared/api/types/cayo-graph";
import ErrorInfo from "@cayo/shared/api/types/ErrorInfo";
import HttpStatus from "@cayo/shared/api/types/HttpStatus";
import { throwException } from "@cayo/shared/api/utils/throwException";
import { showError } from "@cayo/shared/components/App/utils/showError";
import logger from "@cayo/shared/libs/logger";

const log = logger.getLogger("ajax-utils");

const getError = (e: any): Promise<ErrorInfo> => {
  log.error(e);

  if (typeof e === "string") {
    return Promise.resolve({ error_description: e });
  }

  if (e.response) {
    try {
      const responseBody = JSON.parse(e.response);
      log.debug("error", responseBody);
      if (responseBody.message) {
        return Promise.resolve({ error_description: responseBody.message });
      }
    } catch (err) {
      log.error("Failed to parse error response", err);
    }
  }

  if (e.message) {
    return Promise.resolve({ error_description: e.message });
  }

  let text: any;
  try {
    if (e instanceof Response) {
      text = e.text();
    } else {
      const firstError = Object.values(e).shift() as Array<string>;
      let errorDesc: ErrorInfo = { error_description: JSON.stringify(e) };
      if (Array.isArray(firstError) && firstError.length) {
        const firstErrorString = firstError[0];
        errorDesc = {
          error_description:
            typeof firstError[0] === "string" ? firstErrorString : JSON.stringify(firstErrorString),
        };
        return Promise.resolve(errorDesc);
      }

      return Promise.resolve(errorDesc);
    }
  } catch (ee) {
    log.error("Failed to parse response", ee);
    return Promise.resolve({ error_description: e.statusText ?? "Unknown error" });
  }

  return text.then((responseText: string) => {
    const resultData200 = parseResponse<any>(responseText);

    let errorInfo = resultData200 as ErrorInfo;
    if (!errorInfo) {
      errorInfo = {};
    }

    const adminError = errorInfo as AdminErrorInfo;
    if (adminError.isError) {
      errorInfo.error = adminError.errorMessage;
      errorInfo.error_description = adminError.errorMessage;
      errorInfo.severity = "error";
      return Promise.resolve(errorInfo);
    }

    if (!errorInfo.error_description) {
      const resultKeys = resultData200 && Object.keys(resultData200);

      errorInfo.error_description =
        (resultData200 &&
          (resultData200?.message ||
            (resultKeys?.length > 0 ? resultData200[resultKeys[0]] : undefined))) ||
        e.statusText ||
        e.status ||
        responseText ||
        "Unknown error";
    }

    log.debug("response parsed", errorInfo);

    if (errorInfo.severity) {
      errorInfo.severity = fromOdataSeverity(errorInfo.severity);
    }

    return Promise.resolve(errorInfo);
  });
};

const fromOdataSeverity = (severity?: string): Severity | undefined => {
  if (!severity) {
    return undefined;
  }
  severity = severity.toLowerCase();

  if (severity === "info") {
    return "informational";
  }

  return severity as Severity;
};

const handleError = async (e: any) => {
  const error = await ajaxUtils.getError(e);
  let msg = error?.error_description;
  if (Array.isArray(msg)) {
    log.debug(msg);

    msg = msg[0];
  }
  showError(msg);
};

const getHeaders = (response: any) => {
  const headers: any = {};
  if (response.headers && response.headers.forEach) {
    response.headers.forEach((v: any, k: any) => (headers[k] = v));
  }

  return headers;
};

export const parseResponse = <T>(responseText: string) => {
  const resultData200 = undefined;
  try {
    return JSON.parse(responseText) as T;
  } catch {
    return resultData200;
  }
};

export const processErrorResponse = async (response: Response, status: number): Promise<any> => {
  const headers = getHeaders(response);
  const message =
    status === HttpStatus.UNAUTHORIZED || status === HttpStatus.NOT_FOUND
      ? "A server side error occurred."
      : "An unexpected server error occurred.";
  const responseText = await response.text();
  return throwException(message, status, responseText, headers);
};

export const ajaxUtils = {
  getError,
  handleError,
  getHeaders,
  parseResponse,
};
